import { useState, useMemo, useEffect } from 'react';
import { MatchState, Team, Player, TeamState, ValidationError } from '@/types';
import AppHeader from '@/components/AppHeader';
import { getTournament } from '@/lib/supabase';

interface FirstServerSelectionProps {
  matchState: MatchState;
  teams: Team[];
  onSelectServer: (servingTeamId: string, player: Player) => Promise<void>;
  onSignOut: () => void;
  readOnly?: boolean;
}

export default function FirstServerSelection({
  matchState,
  teams,
  onSelectServer,
  onSignOut,
  readOnly = false,
}: FirstServerSelectionProps) {
  const [error, setError] = useState<ValidationError | null>(null);
  const [selecting, setSelecting] = useState<string | null>(null);
  const [tournamentName, setTournamentName] = useState<string | null>(null);
  const [selectedServingTeam, setSelectedServingTeam] = useState<TeamState | null>(null);

  // Load tournament name if match belongs to a tournament
  useEffect(() => {
    const tid = matchState.match?.tournamentId;
    if (!tid) { setTournamentName(null); return; }
    getTournament(tid)
      .then(t => setTournamentName(t.name))
      .catch(() => setTournamentName(null));
  }, [matchState.match?.tournamentId]);

  // Auto-determine first serving team from toss for Set 1
  const firstServingTeam = useMemo(() => {
    const match = matchState.match;
    if (!match) return null;
    const { tossWonById, tossChoice, teamAId, teamBId } = match;
    if (!tossWonById || !tossChoice || !teamAId || !teamBId) return null;
    if (tossChoice === 'serve') {
      return teams.find(t => t.id === tossWonById);
    } else {
      return teams.find(t => t.id === (tossWonById === teamAId ? teamBId : teamAId));
    }
  }, [matchState.match, teams]);

  // Build TeamState for the serving team
  const servingTeamState = useMemo(() => {
    if (!firstServingTeam) return null;
    return {
      ...firstServingTeam,
      setsWon: 0,
      rotationState: { serveHistory: [], cycleServed: [] },
    };
  }, [firstServingTeam]);

  useEffect(() => {
    setSelectedServingTeam(servingTeamState);
  }, [servingTeamState]);

  const handleSelectPlayer = async (player: Player) => {
    if (!servingTeamState) return;
    setSelecting(player.id);
    setError(null);
    try {
      await onSelectServer(servingTeamState.id, player);
    } catch (err) {
      setError({
        id: 'server-error',
        title: 'INVALID FIRST SERVER',
        message: err instanceof Error ? err.message : 'Failed to select server',
      });
    } finally {
      setSelecting(null);
    }
  };

  const dismissError = () => setError(null);

  if (!matchState.match || !selectedServingTeam) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-text-secondary">Loading...</p>
      </div>
    );
  }

  const matchTypeLabel = matchState.match.matchType.charAt(0).toUpperCase() + matchState.match.matchType.slice(1);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header with tournament context */}
      <AppHeader
        onSignOut={onSignOut}
        title={tournamentName || `Match #${matchState.match.matchNumber}`}
        subtitle={tournamentName ? `${matchTypeLabel} Match #${matchState.match.matchNumber}` : 'First Server Selection'}
        tournamentId={matchState.match.tournamentId ?? undefined}
        tournamentName={tournamentName ?? undefined}
      />

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-md mx-auto">
          {/* Server Selection Phase */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-text-primary mb-2">
              SELECT FIRST SERVER
            </h1>
            <p className="text-text-secondary">
              <span className="font-semibold" style={{ color: selectedServingTeam.color }}>
                {selectedServingTeam.name}
              </span>{' '}
              serve first.
            </p>
            <p className="text-text-muted text-sm">
              Choose who opens the match.
            </p>
          </div>

          {/* Team Header */}
          <div
            className="rounded-lg p-3 mb-4"
            style={{ backgroundColor: `${selectedServingTeam.color}15`, borderColor: selectedServingTeam.color, borderWidth: 1 }}
          >
            <p className="font-semibold text-center" style={{ color: selectedServingTeam.color }}>
              {selectedServingTeam.name.toUpperCase()}
            </p>
          </div>

          {readOnly ? (
            <div className="card text-center py-8">
              <p className="text-text-muted text-sm">Waiting for server selection...</p>
              <p className="text-text-muted text-xs mt-1">Scorer will start this match shortly.</p>
            </div>
          ) : (
            /* Player Selection Grid */
            <div className="grid grid-cols-2 gap-3">
              {selectedServingTeam.players.map((player) => {
                const isSelected = selecting === player.id;
                return (
                  <button
                    key={player.id}
                    onClick={() => handleSelectPlayer(player)}
                    disabled={selecting !== null}
                    className="py-5 px-4 rounded-lg font-bold text-xl transition-all active:scale-[0.98] bg-surface-raised"
                    style={{
                      backgroundColor: isSelected ? selectedServingTeam.color : 'rgb(39, 39, 42)',
                      color: '#ffffff',
                      border: `4px solid ${selectedServingTeam.color}`,
                      boxShadow: isSelected ? `0 4px 12px ${selectedServingTeam.color}60` : undefined,
                    }}
                  >
                    {isSelected ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-5 h-5 border-2 border-white rounded-full animate-spin" style={{ borderTopColor: 'transparent' }} />
                        {player.name}
                      </span>
                    ) : (
                      player.name
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Error Popup */}
      {error && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-surface border border-border rounded-xl p-6 max-w-sm w-full animate-fade-in">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-error/20 flex items-center justify-center mx-auto mb-4">
                <span className="text-error text-3xl font-bold">X</span>
              </div>
              <h2 className="text-xl font-bold text-error mb-2">{error.title}</h2>
              <p className="text-text-secondary mb-6">{error.message}</p>
              <button
                onClick={dismissError}
                className="btn-primary bg-surface-raised border border-border text-text-primary"
              >
                UNDERSTOOD
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
